import { Box, Button, useTheme } from "@mui/material";
import { useMemo } from "react";
import { useReminderTypesQuery } from "web_myplann/Api/Reminders/useReminderTypesQuery";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { ReminderFormFields } from "web_myplann/Reminders/Components/ReminderFormFields";
import { useReminderEditForm } from "web_myplann/Reminders/Hooks/useReminderEditForm";
import type {
	Reminder,
	ReminderEntity,
} from "web_myplann/Reminders/Types/ReminderTypes";
import { getEditableTemplates } from "web_myplann/Reminders/Utils/getEditableTemplates";
import { SmallDrawer } from "web_myplann/Shared/Components/SmallDrawer";

type Props = {
	entity: ReminderEntity;
	reminder: Reminder;
	validTo?: Date | null;
	open: boolean;
	onClose: () => void;
};

export const ReminderEditDrawer = ({
	entity,
	reminder,
	validTo = entity.validTo,
	open,
	onClose,
}: Props) => {
	const { t } = useResource();
	const theme = useTheme();

	const { data: templates } = useReminderTypesQuery(entity);

	const { form, submit, entityConfig, isSubmitting } = useReminderEditForm({
		entity,
		reminder,
		onSuccess: onClose,
	});

	const selectableTemplates = useMemo(() => {
		return getEditableTemplates(
			templates ?? [],
			entityConfig,
			validTo,
			reminder.typeKey,
		);
	}, [templates, entityConfig, validTo, reminder.typeKey]);

	return (
		<SmallDrawer
			open={open}
			onClose={onClose}
			title={t(Resources.Reminders.Form.editTitle)}
		>
			<ReminderFormFields
				form={form}
				templates={selectableTemplates}
				entityConfig={entityConfig}
				validTo={validTo}
			/>

			<Box px={theme.spacingTokens.lg} py={theme.spacingTokens.lg}>
				<Button
					variant="primary"
					onClick={submit}
					disabled={!form.formState.isValid}
					loading={isSubmitting}
				>
					{t(Resources.Reminders.Form.editCta)}
				</Button>
			</Box>
		</SmallDrawer>
	);
};
